import apiClient from "../common";
import {
	AbcValue,
	ABC_OPTIONS,
	ProductDistribucion,
	ProductMasterRow,
	ProductMetodoPago,
	ProductProveedor,
	ProductTransporte,
	ProductUnitOfMeasure,
} from "../../views/admin/masters/types";

export type ProductsMasterListParams = {
	page?: number;
	page_size?: number;
	search?: string;
};

export type ProductsMasterApiRow = {
	id: string;
	codigo: string;
	descripcion: string;
	categoria: string;
	linea: string;
	sublinea: string;
	sublinea_en: string;
	abc: string;
	estado: string;
	tipo_material: string;
	proveedor: {
		codigo: string;
		descripcion: string;
		descripcion_producto_proveedor: string;
		email: string;
		nombre_contacto: string;
		pais: string;
	};
	distribucion: {
		costo_fob_unidad: string;
		costo_fob_bulto: string;
		unidad_despacho: string;
		conteo_despacho: string;
	};
	transporte: {
		cpe: string;
		codigo_arancelario: string;
		moq: string;
		tiempo_promedio_produccion: string;
		puerto1: string;
		puerto2: string;
		puerto3: string;
	};
	metodo_pago: {
		advance: string;
		balance: string;
	};
	unidades_medida: Array<{
		producto: string;
		codigo_unidad: string;
		total_unidades: string;
		codigo_barra: string;
		alto: string;
		ancho: string;
		largo: string;
		peso: string;
	}>;
	has_millennials_master: boolean;
};

export type ProductsMasterListResponse = {
	count: number;
	page: number;
	page_size: number;
	total_pages: number;
	results: ProductsMasterApiRow[];
};

export type ProductsMasterBulkRowPayload = Omit<
	ProductsMasterApiRow,
	"id" | "has_millennials_master"
>;

export type ProductsMasterBulkUploadResponse = {
	created: number;
	updated: number;
	datamart_sync_queued: number;
	errors: Array<{ row: number; codigo: string; message: string }>;
	message: string;
};

export type ProductsMasterUpdatePayload = {
	sublinea_en?: string;
	abc?: AbcValue;
	proveedor?: {
		codigo?: string;
		descripcion?: string;
		descripcion_producto_proveedor?: string;
		email?: string;
		nombre_contacto?: string;
		pais?: string;
	};
	distribucion?: {
		costo_fob_unidad?: string;
		costo_fob_bulto?: string;
		unidad_despacho?: string;
		conteo_despacho?: string;
	};
	transporte?: {
		cpe?: string;
		codigo_arancelario?: string;
		moq?: string;
		tiempo_promedio_produccion?: string;
		puerto1?: string;
		puerto2?: string;
		puerto3?: string;
	};
	metodo_pago?: {
		advance?: string;
		balance?: string;
	};
};

/**
 * Sanea el ABC que llega del API a los valores del catálogo.
 *
 * El backend ya normaliza y valida A/B/C, pero el tipo del API es string:
 * cualquier valor fuera del catálogo se degrada a '' en lugar de romper la UI.
 */
function mapAbc(value: string | null | undefined): AbcValue {
	const normalized = (value ?? "").trim().toUpperCase();
	return (ABC_OPTIONS as string[]).includes(normalized)
		? (normalized as AbcValue)
		: "";
}

function mapProveedor(row: ProductsMasterApiRow["proveedor"]): ProductProveedor {
	return {
		codigo: row.codigo ?? "",
		descripcion: row.descripcion ?? "",
		descripcionProductoProveedor: row.descripcion_producto_proveedor ?? "",
		email: row.email ?? "",
		nombreContacto: row.nombre_contacto ?? "",
		pais: row.pais ?? "",
	};
}

function mapDistribucion(row: ProductsMasterApiRow["distribucion"]): ProductDistribucion {
	return {
		costoFobUnidad: row.costo_fob_unidad ?? "",
		costoFobBulto: row.costo_fob_bulto ?? "",
		unidadDespacho: row.unidad_despacho ?? "",
		conteoDespacho: row.conteo_despacho ?? "",
	};
}

function mapTransporte(row: ProductsMasterApiRow["transporte"]): ProductTransporte {
	return {
		cpe: row.cpe ?? "",
		codigoArancelario: row.codigo_arancelario ?? "",
		moq: row.moq ?? "",
		tiempoPromedioProduccion: row.tiempo_promedio_produccion ?? "",
		puerto1: row.puerto1 ?? "",
		puerto2: row.puerto2 ?? "",
		puerto3: row.puerto3 ?? "",
	};
}

function mapMetodoPago(row: ProductsMasterApiRow["metodo_pago"]): ProductMetodoPago {
	return {
		advance: row.advance ?? "",
		balance: row.balance ?? "",
	};
}

function mapUnits(
	units: ProductsMasterApiRow["unidades_medida"],
): ProductUnitOfMeasure[] {
	return (units ?? []).map((unit) => ({
		producto: unit.producto ?? "",
		codigoUnidad: unit.codigo_unidad ?? "",
		totalUnidades: unit.total_unidades ?? "",
		codigoBarra: unit.codigo_barra ?? "",
		alto: unit.alto ?? "",
		ancho: unit.ancho ?? "",
		largo: unit.largo ?? "",
		peso: unit.peso ?? "",
	}));
}

export function mapProductsMasterRow(row: ProductsMasterApiRow): ProductMasterRow {
	return {
		id: row.id,
		codigo: row.codigo,
		descripcion: row.descripcion,
		categoria: row.categoria,
		linea: row.linea,
		sublinea: row.sublinea,
		sublineaEn: row.sublinea_en ?? "",
		abc: mapAbc(row.abc),
		estado: row.estado,
		tipoMaterial: row.tipo_material,
		proveedor: mapProveedor(row.proveedor),
		distribucion: mapDistribucion(row.distribucion),
		transporte: mapTransporte(row.transporte),
		metodoPago: mapMetodoPago(row.metodo_pago),
		unidadesMedida: mapUnits(row.unidades_medida),
	};
}

export function toProductsMasterUpdatePayload(
	product: ProductMasterRow,
): ProductsMasterUpdatePayload {
	return {
		sublinea_en: product.sublineaEn,
		abc: product.abc,
		proveedor: {
			codigo: product.proveedor.codigo,
			descripcion: product.proveedor.descripcion,
			descripcion_producto_proveedor: product.proveedor.descripcionProductoProveedor,
			email: product.proveedor.email,
			nombre_contacto: product.proveedor.nombreContacto,
			pais: product.proveedor.pais,
		},
		distribucion: {
			costo_fob_unidad: product.distribucion.costoFobUnidad,
			costo_fob_bulto: product.distribucion.costoFobBulto,
			unidad_despacho: product.distribucion.unidadDespacho,
			conteo_despacho: product.distribucion.conteoDespacho,
		},
		transporte: {
			cpe: product.transporte.cpe,
			codigo_arancelario: product.transporte.codigoArancelario,
			moq: product.transporte.moq,
			tiempo_promedio_produccion: product.transporte.tiempoPromedioProduccion,
			puerto1: product.transporte.puerto1,
			puerto2: product.transporte.puerto2,
			puerto3: product.transporte.puerto3,
		},
		metodo_pago: {
			advance: product.metodoPago.advance,
			balance: product.metodoPago.balance,
		},
	};
}

export function toProductsMasterBulkRow(
	product: ProductMasterRow,
): ProductsMasterBulkRowPayload {
	return {
		codigo: product.codigo,
		descripcion: product.descripcion,
		categoria: product.categoria,
		linea: product.linea,
		sublinea: product.sublinea,
		sublinea_en: product.sublineaEn,
		abc: product.abc,
		estado: product.estado,
		tipo_material: product.tipoMaterial,
		proveedor: {
			codigo: product.proveedor.codigo,
			descripcion: product.proveedor.descripcion,
			descripcion_producto_proveedor: product.proveedor.descripcionProductoProveedor,
			email: product.proveedor.email,
			nombre_contacto: product.proveedor.nombreContacto,
			pais: product.proveedor.pais,
		},
		distribucion: {
			costo_fob_unidad: product.distribucion.costoFobUnidad,
			costo_fob_bulto: product.distribucion.costoFobBulto,
			unidad_despacho: product.distribucion.unidadDespacho,
			conteo_despacho: product.distribucion.conteoDespacho,
		},
		transporte: {
			cpe: product.transporte.cpe,
			codigo_arancelario: product.transporte.codigoArancelario,
			moq: product.transporte.moq,
			tiempo_promedio_produccion: product.transporte.tiempoPromedioProduccion,
			puerto1: product.transporte.puerto1,
			puerto2: product.transporte.puerto2,
			puerto3: product.transporte.puerto3,
		},
		metodo_pago: {
			advance: product.metodoPago.advance,
			balance: product.metodoPago.balance,
		},
		unidades_medida: product.unidadesMedida.map((unit) => ({
			producto: unit.producto,
			codigo_unidad: unit.codigoUnidad,
			total_unidades: unit.totalUnidades,
			codigo_barra: unit.codigoBarra,
			alto: unit.alto,
			ancho: unit.ancho,
			largo: unit.largo,
			peso: unit.peso,
		})),
	};
}

export const PRODUCTS_MASTER_TEMPLATE_URL =
	(process.env.REACT_APP_TEMPLATE_URL ?? "") + "products_master/template.xlsx";

export const ApiProductsMasterList = async (params: ProductsMasterListParams = {}) => {
	const search = new URLSearchParams();
	if (params.page != null) search.set("page", String(params.page));
	if (params.page_size != null) search.set("page_size", String(params.page_size));
	if (params.search) search.set("search", params.search);
	const qs = search.toString();
	const suffix = qs ? `?${qs}` : "";
	return await apiClient.get<ProductsMasterListResponse>(
		`masters/products/${suffix}`,
		{ withCredentials: true },
	);
};

export const ApiProductsMasterGet = async (sku: string) => {
	return await apiClient.get<ProductsMasterApiRow>(
		`masters/products/${encodeURIComponent(sku)}/`,
		{ withCredentials: true },
	);
};

export const ApiProductsMasterPatch = async (
	sku: string,
	payload: ProductsMasterUpdatePayload,
) => {
	return await apiClient.patch<ProductsMasterApiRow>(
		`masters/products/${encodeURIComponent(sku)}/`,
		payload,
		{ withCredentials: true },
	);
};

export const ApiProductsMasterBulkUpload = async (rows: ProductsMasterBulkRowPayload[]) => {
	return await apiClient.post<ProductsMasterBulkUploadResponse>(
		"masters/products/bulk/",
		{ rows },
		{ withCredentials: true },
	);
};

export const ApiProductsMasterBulkUploadFile = async (file: File) => {
	const form = new FormData();
	form.append("file", file);
	return await apiClient.post<ProductsMasterBulkUploadResponse>(
		"masters/products/bulk/file/",
		form,
		{
			withCredentials: true,
			headers: { "Content-Type": "multipart/form-data" },
		},
	);
};
